import * as stylex from '@stylexjs/stylex';
import { FC, Fragment, useEffect, useRef, MutableRefObject } from 'react';
import { styles } from './styles';
import { formatCheckInTime } from '@shared/libs/format-check-in-time';
import { getScanTypeLabel } from '@features/participant-card-no-segment/lib/get-scan-type-label';
import { Guest, Checkbox, Radiobutton } from '../../model';

export type GuestType = 'main' | 'adult' | 'child';

interface GuestInfoDrawerProps {
  mainGuest: Guest;
  adultGuests?: Guest[];
  childrenGuests?: Guest[];
  checkboxes?: Checkbox[];
  radiobuttons?: Radiobutton[];
  scrollToGuest?: { type: GuestType; index?: number };
}

const getFieldLabel = (key: string): string => {
  const fieldLabels: Record<string, string> = {
    phone: 'Телефон',
    email: 'E-mail',
    age: 'Возраст',
  };
  return fieldLabels[key] || key;
};

const renderGuestInfo = (
  guest: Guest,
  badgeText: string,
  isMainGuest: boolean = false,
  checkboxes?: Checkbox[],
  radiobuttons?: Radiobutton[],
  guestRef?: (node: HTMLDivElement | null) => void
) => {
  const { date: regDate, time: regTime } = formatCheckInTime(guest.checkInTime);
  const registrationText = regDate !== '-' && regTime !== '-' ? `${regDate} в ${regTime}` : '-';

  const fullName = ['lastName' in guest && guest.lastName, guest.firstName, 'surname' in guest && guest.surname]
    .filter(Boolean)
    .join(' ');

  const badgeStyle = isMainGuest ? styles.badge : styles.badgeSecondary;
  const badgeTextStyle = isMainGuest ? styles.badgeText : styles.badgeTextSecondary;

  const excludedFields = new Set([
    'firstName',
    'surname',
    'lastName',
    'ticketId',
    'checkInTime',
    'organizerName',
    'scanType',
    'participantId',
    'ticketType',
    'additionalFields',
    '__typename',
  ]);

  return (
    <div ref={guestRef} {...stylex.props(styles.infoGroup)}>
      {fullName && (
        <div {...stylex.props(styles.titleBlock)}>
          <div {...stylex.props(styles.guestName)}>{fullName}</div>
          <div {...stylex.props(badgeStyle)}>
            <div {...stylex.props(badgeTextStyle)}>{badgeText}</div>
          </div>
        </div>
      )}
      <div {...stylex.props(styles.infoTable)}>
        {Object.entries(guest).map(([key, value]) => {
          if (excludedFields.has(key) || !value) return null;

          return (
            <div key={key} {...stylex.props(styles.dataRow)}>
              <div {...stylex.props(styles.labelContainer)}>
                <div {...stylex.props(styles.fieldLabel)}>{getFieldLabel(key)}</div>
                <div {...stylex.props(styles.dottedLine)}></div>
              </div>
              <div {...stylex.props(styles.fieldValue)}>{value}</div>
            </div>
          );
        })}
        {guest.additionalFields?.map((field, index) => (
          <div key={index} {...stylex.props(styles.dataRow)}>
            <div {...stylex.props(styles.labelContainer)}>
              <div {...stylex.props(styles.fieldLabel)}>{field.key}</div>
              <div {...stylex.props(styles.dottedLine)}></div>
            </div>
            <div {...stylex.props(styles.fieldValue)}>{field.value}</div>
          </div>
        ))}
        <div {...stylex.props(styles.dataRow)}>
          <div {...stylex.props(styles.labelContainer)}>
            <div {...stylex.props(styles.fieldLabel)}>Регистрация</div>
            <div {...stylex.props(styles.dottedLine)}></div>
          </div>
          <div {...stylex.props(styles.fieldValue)}>
            {registrationText && registrationText !== '-' ? registrationText : '-'}
          </div>
        </div>
        <div {...stylex.props(styles.dataRow)}>
          <div {...stylex.props(styles.labelContainer)}>
            <div {...stylex.props(styles.fieldLabel)}>Регистратор</div>
            <div {...stylex.props(styles.dottedLine)}></div>
          </div>
          <div {...stylex.props(styles.fieldValue)}>{guest.organizerName ? guest.organizerName : '-'}</div>
        </div>
        <div {...stylex.props(styles.dataRow)}>
          <div {...stylex.props(styles.labelContainer)}>
            <div {...stylex.props(styles.fieldLabel)}>Способ</div>
            <div {...stylex.props(styles.dottedLine)}></div>
          </div>
          <div {...stylex.props(styles.fieldValue)}>{getScanTypeLabel(guest.scanType) || '-'}</div>
        </div>
        {isMainGuest &&
          checkboxes?.map((checkbox, index) => {
            const hasValues = checkbox.valueCheckboxes?.length;
            if (!hasValues) return null;

            return (
              <div key={index} {...stylex.props(styles.details)}>
                {(checkbox.titleCheckbox || checkbox.titleCheckboxes) && (
                  <div {...stylex.props(styles.detailsTitle)}>{checkbox.titleCheckbox || checkbox.titleCheckboxes}</div>
                )}
                <div {...stylex.props(styles.detailsFrame)}>
                  {checkbox.valueCheckboxes.map((value, valueIndex) => (
                    <div key={valueIndex} {...stylex.props(styles.detailsFrameCheck)}>
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        {isMainGuest &&
          radiobuttons?.map((radiobutton, index) => {
            const hasValues = radiobutton.valueRadiobutton?.length;
            if (!hasValues) return null;

            return (
              <div key={index} {...stylex.props(styles.details)}>
                {radiobutton.titleRadiobutton && (
                  <div {...stylex.props(styles.detailsTitle)}>{radiobutton.titleRadiobutton}</div>
                )}
                <div {...stylex.props(styles.detailsFrame)}>
                  {radiobutton.valueRadiobutton.map((value, valueIndex) => (
                    <div key={valueIndex} {...stylex.props(styles.detailsFrameCheck)}>
                      {value}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const GuestInfoDrawer: FC<GuestInfoDrawerProps> = ({
  mainGuest,
  adultGuests,
  childrenGuests,
  checkboxes,
  radiobuttons,
  scrollToGuest,
}) => {
  const mainGuestRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement | null>;
  const adultGuestsRefs = useRef<Record<number, HTMLDivElement>>({});
  const childrenGuestsRefs = useRef<Record<number, HTMLDivElement>>({});

  useEffect(() => {
    if (!scrollToGuest) return;

    const scrollToTarget = () => {
      if (scrollToGuest.type === 'main') {
        if (mainGuestRef.current) {
          mainGuestRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      }

      if (scrollToGuest.type === 'adult' && scrollToGuest.index !== undefined) {
        const targetElement = adultGuestsRefs.current[scrollToGuest.index];
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      }

      if (scrollToGuest.type === 'child' && scrollToGuest.index !== undefined) {
        const targetElement = childrenGuestsRefs.current[scrollToGuest.index];
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      }

      return false;
    };

    const rafId = requestAnimationFrame(() => {
      if (!scrollToTarget()) {
        setTimeout(scrollToTarget, 100);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [scrollToGuest]);

  return (
    <div {...stylex.props(styles.drawerContent)}>
      {renderGuestInfo(mainGuest, 'Основной гость', true, checkboxes, radiobuttons, (node) => {
        mainGuestRef.current = node;
      })}
      {adultGuests?.map((adultGuest, index) => (
        <Fragment key={index}>
          {renderGuestInfo(adultGuest, 'Взрослый гость', false, undefined, undefined, (node) => {
            if (node) {
              adultGuestsRefs.current[index] = node;
              return;
            }
            delete adultGuestsRefs.current[index];
          })}
        </Fragment>
      ))}
      {childrenGuests?.map((childrenGuest, index) => (
        <Fragment key={index}>
          {renderGuestInfo(childrenGuest, 'Маленький гость', false, undefined, undefined, (node) => {
            if (node) {
              childrenGuestsRefs.current[index] = node;
              return;
            }
            delete childrenGuestsRefs.current[index];
          })}
        </Fragment>
      ))}
    </div>
  );
};
